import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguageDirection } from '@/shared/hooks';
import { cn } from '@/shared/lib/utils';

interface GoalProgressNavigationProps {
  currentIndex: number;
  totalGoals: number;
  onPrevious: () => void;
  onNext: () => void;
  isLastGoal: boolean;
}

export const GoalProgressNavigation = ({
  currentIndex,
  totalGoals,
  onPrevious,
  onNext,
  isLastGoal,
}: GoalProgressNavigationProps) => {
  const { t } = useTranslation('oneOnOne');
  const { isRTL } = useLanguageDirection();

  const progressPercentage = useMemo(() => {
    if (totalGoals === 0) return 0;
    return Math.round(((currentIndex + 1) / totalGoals) * 100);
  }, [currentIndex, totalGoals]);

  return (
    <div className="flex items-center gap-6 flex-1 md:flex-initial justify-center md:justify-end">
      <Button
        variant="outline"
        onClick={onPrevious}
        disabled={currentIndex === 0}
        className="shrink-0 flex items-center gap-2 rounded-xl px-5 py-2.5"
      >
        {isRTL ? (
          <ArrowRightIcon className="w-4 h-4" />
        ) : (
          <ArrowLeftIcon className="w-4 h-4" />
        )}
        {t('previous')}
      </Button>

      <div className="flex flex-col items-center gap-2 min-w-[200px]">
        <p className="text-2xl font-semibold text-slate-900 whitespace-nowrap">
          {t('goalLabel')}{' '}
          <span className="text-primary">{currentIndex + 1}</span> {t('of')}{' '}
          {totalGoals}
        </p>
        <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute top-0 start-0 h-full bg-primary transition-all duration-300 rounded-full"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      <Button
        onClick={onNext}
        className={cn(
          'shrink-0',
          isLastGoal && 'bg-emerald-600 hover:bg-emerald-500'
        )}
      >
        {isLastGoal ? t('completeReview') : t('next')}
      </Button>
    </div>
  );
};
